/**
 * Utility for smooth scrolling to sections on a page.
 * Designed to be reusable across the application.
 */

export interface ScrollToSectionOptions {
    /** The ID of the element to scroll to (without the # prefix) */
    sectionId: string;
    /** The path(s) where this scroll should work. Can be a string or array of strings. */
    allowedPaths?: string | string[];
    /** Offset from the top of the section (useful for fixed headers). Default: 80 */
    offset?: number;
    /** Scroll behavior. Default: 'smooth' */
    behavior?: ScrollBehavior;
    /** Callback to run after scrolling (e.g., close mobile menu) */
    onAfterScroll?: () => void;
    /** If true, navigates to the allowed path first (if not already there) then scrolls */
    navigateIfNeeded?: boolean;
}

/**
 * Scrolls to a section on the page if the current path matches allowed paths.
 * Works consistently across all screen sizes and browser types.
 * 
 * @example
 * // Basic usage
 * scrollToSection({ sectionId: 'pricing' });
 * 
 * @example
 * // With options
 * scrollToSection({
 *   sectionId: 'pricing',
 *   allowedPaths: '/landing-page',
 *   offset: 100,
 *   onAfterScroll: () => closeMobileMenu()
 * });
 */
export const scrollToSection = (options: ScrollToSectionOptions): boolean => {
    const {
        sectionId,
        allowedPaths = '/landing-page',
        offset = 80,
        behavior = 'smooth',
        onAfterScroll,
        navigateIfNeeded = false,
    } = options;

    const currentPath = window.location.pathname;
    
    // Normalize allowed paths to array
    const pathsArray = Array.isArray(allowedPaths) ? allowedPaths : [allowedPaths];
    
    // Check if current path matches any allowed path
    const isOnAllowedPath = pathsArray.some(path => 
        currentPath === path || currentPath.startsWith(path)
    );

    // If not on an allowed path
    if (!isOnAllowedPath) {
        if (navigateIfNeeded && pathsArray.length > 0) {
            // Navigate to the first allowed path with the section hash
            const targetPath = `${pathsArray[0]}#${sectionId}`;
            window.location.href = targetPath;
            return true;
        }
        return false;
    }

    // Find the target element
    const targetElement = document.getElementById(sectionId);
    
    if (!targetElement) {
        console.warn(`scrollToSection: Element with id "${sectionId}" not found`);
        return false;
    }

    // Calculate the scroll position
    const elementRect = targetElement.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const scrollPosition = absoluteElementTop - offset;

    // Perform the scroll
    window.scrollTo({
        top: scrollPosition,
        behavior: behavior,
    });

    // Execute callback after scroll animation completes
    if (onAfterScroll) {
        // Use a reasonable timeout for smooth scroll completion
        const scrollDuration = behavior === 'smooth' ? 500 : 0;
        setTimeout(onAfterScroll, scrollDuration);
    }

    return true;
};

/**
 * Creates a click handler for navigation buttons that scroll to sections.
 * Useful for navigation components where you need consistent scroll behavior.
 * 
 * @example
 * // In a React component
 * const handlePricingClick = createScrollHandler({
 *   sectionId: 'pricing-section',
 *   onAfterScroll: () => setMobileMenuOpen(false)
 * });
 * 
 * <button onClick={handlePricingClick}>Pricing</button>
 */
export const createScrollHandler = (options: ScrollToSectionOptions) => {
    return (event?: React.MouseEvent | MouseEvent) => {
        if (event) {
            event.preventDefault();
        }
        scrollToSection(options);
    };
};

/**
 * Hook-friendly scroll function that handles hash-based navigation on page load.
 * Call this in a useEffect to handle direct URL navigation with hash.
 * 
 * @example
 * useEffect(() => {
 *   handleHashScroll({ offset: 80 });
 * }, []);
 */
export const handleHashScroll = (options?: { offset?: number; behavior?: ScrollBehavior }) => {
    const { offset = 80, behavior = 'smooth' } = options || {};
    
    const hash = window.location.hash;
    if (hash) {
        const sectionId = hash.replace('#', '');
        // Small delay to ensure DOM is ready
        setTimeout(() => {
            scrollToSection({
                sectionId,
                allowedPaths: window.location.pathname,
                offset,
                behavior,
            });
        }, 100);
    }
};

// Section ID constants for the landing page
// Add new section IDs here as you expand the landing page
export const LANDING_PAGE_SECTIONS = {
    PRICING: 'pricing-section',
    HERO: 'hero-section',
    PRODUCTS: 'products-section',
    INDUSTRIES: 'industries-section',
    TESTIMONIALS: 'testimonials-section',
    CONTACT: 'contact-section',
} as const;

export type LandingPageSection = typeof LANDING_PAGE_SECTIONS[keyof typeof LANDING_PAGE_SECTIONS];

